<template>
  <section class="admin-content" id="payment-run">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 28px 0; gap: 12px;">
            <div class="header-icon-wrap"><i class="fe fe-send" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <small style="opacity: 0.8;"><router-link to="/online-payments?tab=runs" class="text-white"><i class="fe fe-arrow-left mr-1"></i>Online payments</router-link></small>
              <h4 class="mb-0 font-weight-bold" v-if="d">Payment run #{{ d.run.id }}: {{ d.run.kind === "capitation" ? "Capitation " + monthLabel(d.run.period) : "Claims" }}</h4>
            </div>
            <span v-if="d" class="run-status big" :class="d.run.status">{{ RUN_STATUS[d.run.status] || d.run.status }}</span>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5" v-if="d">
          <div class="card shadow-sm table-card mb-3">
            <div class="card-body d-flex flex-wrap align-items-center" style="gap: 24px;">
              <div><span class="stat-label">To pay</span><div class="stat-value">₦{{ fmt(d.run.total_amount) }}</div></div>
              <div><span class="stat-label">Paid</span><div class="stat-value text-success">₦{{ fmt(sum("succeeded")) }}</div></div>
              <div><span class="stat-label">Facilities</span><div class="stat-value">{{ d.items.length }}</div><small class="text-muted">{{ countsText }}</small></div>
              <div class="small text-muted">
                Prepared by <b>{{ d.prepared_by_name }}</b> on {{ d.run.created_at | moment("D MMM YYYY, h:mm a") }}<br />
                <span v-if="d.run.approved_by">Approved by <b>{{ d.approved_by_name }}</b> on {{ d.run.approved_at | moment("D MMM YYYY, h:mm a") }}<br />From {{ d.run.debit_account_name }}</span>
              </div>
              <div class="ml-auto d-flex flex-wrap" style="gap: 8px;">
                <button v-if="d.can_approve" class="btn btn-approve" :disabled="busy || !d.enabled" @click="approveOpen = true" :title="d.enabled ? '' : 'Online payments are switched off on the server'">
                  <i class="fe fe-check-circle mr-1"></i> Approve and pay ₦{{ fmt(d.run.total_amount) }}
                </button>
                <button v-if="d.can_approve || d.can_cancel" class="btn btn-outline-danger" :disabled="busy" @click="reject">{{ d.can_cancel ? "Cancel run" : "Reject" }}</button>
                <button v-if="d.run.status === 'processing'" class="btn btn-outline-primary" :disabled="busy" @click="reconcile"><i class="fe fe-refresh-cw mr-1"></i> Check with the bank</button>
                <button v-if="d.can_retry" class="btn btn-outline-warning" :disabled="busy" @click="retryOpen = true"><i class="fe fe-rotate-ccw mr-1"></i> Retry failed</button>
              </div>
            </div>
            <div v-if="d.run.status === 'pending_approval' && !d.can_approve && !d.can_cancel" class="px-4 pb-3 small text-muted">
              Waiting for an agency admin, the Executive Secretary or a Director (not the person who prepared it) to approve.
            </div>
            <div v-if="d.run.note" class="px-4 pb-3 small"><b>Note:</b> {{ d.run.note }}</div>
          </div>

          <div class="card shadow-sm table-card">
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th">Details</th><th class="table-th text-right">Amount</th><th class="table-th">Pays to</th><th class="table-th">Status</th></tr></thead>
                <tbody>
                  <tr v-for="i in d.items" :key="i.id" class="table-row">
                    <td>{{ i.facility }}</td>
                    <td class="small">
                      <template v-if="i.breakdown && i.breakdown.lines">{{ i.breakdown.lines.map((l) => `${l.sector} ${fmt(l.enrollees)} × ₦${fmt(l.rate)}`).join(" · ") }}</template>
                      <template v-else-if="i.breakdown">Payment order {{ i.breakdown.payment_number }} · {{ i.breakdown.claims }} claims</template>
                    </td>
                    <td class="text-right font-weight-bold">₦{{ fmt(i.amount) }}</td>
                    <td class="small">
                      <span v-if="i.dest_account">{{ i.dest_kind === "wallet" ? "Wallet" : "Bank" }} {{ i.dest_account }}<div class="text-muted">{{ i.dest_account_name }}</div></span>
                      <span v-else class="text-danger">No payout account
                        <router-link :to="{ path: '/payout-account', query: { provider_id: i.provider_id } }" class="ml-1">Add</router-link></span>
                    </td>
                    <td>
                      <span class="item-status" :class="i.status">{{ ITEM_STATUS[i.status] || i.status }}</span>
                      <div v-if="i.error" class="small text-danger mt-1">{{ i.error }}</div>
                      <div v-if="i.reference" class="small text-muted">Ref {{ i.reference }}</div>
                      <router-link v-if="i.paymentorder_id" :to="`/claims/transaction/${i.paymentorder_id}`" class="small">Payment order</router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else class="container"><div class="card shadow-sm"><div class="card-body empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div></div>
      </section>

      <!-- password confirmation for approve / retry -->
      <div v-if="approveOpen || retryOpen" class="pr-backdrop" @click.self="approveOpen = retryOpen = false">
        <div class="pr-dialog">
          <h5 class="mb-1">{{ approveOpen ? "Approve and send the money" : "Send the failed payments again" }}</h5>
          <p class="small text-muted">
            {{ approveOpen ? `₦${fmt(d.run.total_amount)} will be transferred from ${d.run.debit_account_name || "the agency's account"} to ${payable} facilities. This cannot be undone.` : "Each failed payment is sent again with a new reference." }}
          </p>
          <label class="small font-weight-bold text-secondary">Your password</label>
          <input type="password" class="form-control mb-3" v-model="password" autocomplete="current-password" @keyup.enter="confirmAction" />
          <div class="d-flex justify-content-end" style="gap: 8px;">
            <button class="btn btn-outline-secondary" :disabled="busy" @click="approveOpen = retryOpen = false">Cancel</button>
            <button class="btn btn-approve" :disabled="busy || !password" @click="confirmAction">
              <span v-if="busy" class="spinner-border spinner-border-sm mr-1"></span>{{ busy ? "Sending…" : "Confirm" }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import moment from "moment";
import { RUN_STATUS, ITEM_STATUS } from "./runStatus";

/** One payment run (payments/runs/{id}): approve (password) and send, reject/cancel, check with the bank, retry failed. */
export default {
  components: { Navbar },
  data() {
    return { d: null, busy: false, approveOpen: false, retryOpen: false, password: "", RUN_STATUS, ITEM_STATUS, timer: null };
  },
  computed: {
    payable() { return this.d ? this.d.items.filter((i) => i.status === "pending").length : 0; },
    countsText() {
      const c = {};
      (this.d ? this.d.items : []).forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
      return Object.entries(c).map(([k, n]) => `${n} ${(ITEM_STATUS[k] || k).toLowerCase()}`).join(" · ");
    },
  },
  created() { this.load(); },
  beforeDestroy() { clearTimeout(this.timer); },
  watch: { "$route.params.id"() { this.load(); } },
  methods: {
    fmt(v) { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },
    monthLabel(p) { return p ? moment(p, "YYYY-MM").format("MMMM YYYY") : ""; },
    sum(status) { return this.d.items.filter((i) => i.status === status).reduce((n, i) => n + i.amount, 0); },
    msg(e, f) { const d = e.response && e.response.data; return (d && (d.errors ? Object.values(d.errors).flat()[0] : d.message)) || f; },
    load() {
      clearTimeout(this.timer);
      this.axios.get(`/api/v1/auth/payments/runs/${this.$route.params.id}`).then((r) => {
        this.d = r.data.data;
        // while transfers are being sent, refresh every 20 s
        if (["approved", "processing"].includes(this.d.run.status)) this.timer = setTimeout(this.load, 20000);
      });
    },
    act(promise, okFallback) {
      this.busy = true;
      return promise
        .then((r) => { this.$toasted.success(r.data.message || okFallback, { position: "top-center", duration: 7000 }); })
        .catch((e) => this.$toasted.error(this.msg(e, "That did not work."), { position: "top-center", duration: 7000 }))
        .finally(() => { this.busy = false; this.password = ""; this.approveOpen = this.retryOpen = false; this.load(); });
    },
    confirmAction() {
      const url = `/api/v1/auth/payments/runs/${this.d.run.id}/` + (this.approveOpen ? "approve" : "retry");
      this.act(this.axios.post(url, { password: this.password, mode: "now" }, { timeout: 0 }), "Done.");
    },
    reject() {
      const note = window.prompt(this.d.can_cancel ? "Cancel this run? Optional reason:" : "Reason for rejecting:");
      if (note === null) return;
      this.act(this.axios.post(`/api/v1/auth/payments/runs/${this.d.run.id}/reject`, { note }), "Done.");
    },
    reconcile() { this.act(this.axios.post(`/api/v1/auth/payments/runs/${this.d.run.id}/reconcile`), "Checked."); },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.btn-approve { background: linear-gradient(135deg, #047857 0%, #10b981 100%); border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 9px 16px; }
.btn-approve:disabled { opacity: 0.5; }
.run-status, .item-status { font-size: 0.72rem; font-weight: 600; padding: 3px 10px; border-radius: 999px; background: #e2e8f0; color: #334155; white-space: nowrap; display: inline-block; }
.run-status.big { font-size: 0.85rem; padding: 6px 14px; }
.run-status.pending_approval, .item-status.pending { background: #fef3c7; color: #92400e; }
.run-status.processing, .run-status.approved, .item-status.sent { background: #dbeafe; color: #1d4ed8; }
.run-status.completed, .item-status.succeeded { background: #d1fae5; color: #065f46; }
.run-status.completed_with_errors, .run-status.rejected, .item-status.failed, .item-status.no_account { background: #fee2e2; color: #991b1b; }
.pr-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 80px 16px; }
.pr-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 440px; padding: 20px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25); }
</style>
